import classNames from 'classnames';
import { FC, ReactElement, useCallback, useMemo } from 'react';
import MultiChoiceOption from './components/MultiChoiceOption';
import { localeStore } from 'src/mobx/localesStore';
import { ICurrencySettings } from 'src/types/branches';
import { IResponseProductModifier, IResponseProductModifierOption } from 'src/types/products';
import { getTranslate } from 'src/utils/helpers';
import './style.scss';

interface IProps {
  error: boolean;
  modifier: IResponseProductModifier;
  onChange: (modifier: IResponseProductModifier) => void;
  leftToMaximum: number;
  currencySettings?: ICurrencySettings;
}

const MultiChoice: FC<IProps> = ({ modifier, ...props }: IProps): ReactElement => {
  const onPressOption = useCallback(
    (option: IResponseProductModifierOption) => {
      const options: IResponseProductModifierOption[] = modifier.options.map((_option) => {
        if (_option.id === option.id) {
          return { ...option, selected: !option.selected };
        }
        return _option;
      });

      props.onChange({ ...modifier, options });
    },
    [modifier, modifier.options]
  );

  const onOptionQuantityChange = useCallback(
    (optionId: number, quantity: number) => {
      const options: IResponseProductModifierOption[] = modifier.options.map((option) => {
        if (option.id === optionId) {
          return { ...option, quantity, selected: true };
        }
        return option;
      });

      props.onChange({ ...modifier, options });
    },
    [modifier.options]
  );

  const message = useMemo((): string => {
    switch (true) {
      case Boolean(modifier.minimum && modifier.maximum):
        return `${localeStore.t('txt_select_from')} ${modifier.minimum} ${localeStore.t('txt_select_to')} ${modifier.maximum}`;
      case Boolean(modifier.minimum):
        return `${localeStore.t('txt_select_from')} ${modifier.minimum}`;
      case Boolean(modifier.maximum):
        return `${localeStore.t('txt_select_maximum')} ${modifier.maximum}`;
      default:
        return localeStore.t('txt_select_any_amount');
    }
  }, [modifier.maximum, modifier.minimum]);

  return (
    <div className="multi-choice">
      <h3 className="multi-choice__title">{getTranslate(modifier.name)}</h3>
      <p className={classNames('multi-choice__subtitle', { error: props.error })}>{message}</p>
      {modifier.options.map((option) => (
        <MultiChoiceOption
          key={option.id}
          uniq={Boolean(modifier.unique_options) || modifier.maximum === 1}
          option={option}
          onPressOption={onPressOption}
          onChangeOptionQuantity={onOptionQuantityChange}
          leftToMaximum={props.leftToMaximum}
          currencySettings={props.currencySettings}
        />
      ))}
    </div>
  );
};

export default MultiChoice;
